import type { SubscriptionTier, SubscriptionStatus } from '@atsready/shared';
import { request } from './api-client';
import type { PaginatedResponse, AdminSubscription } from './types';

export type { AdminSubscription };

export async function listSubscriptions(params?: {
  page?: number;
  pageSize?: number;
  tier?: SubscriptionTier;
  status?: SubscriptionStatus;
}): Promise<PaginatedResponse<AdminSubscription>> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  if (params?.tier) query.set('tier', params.tier);
  if (params?.status) query.set('status', params.status);

  return request(`/subscriptions?${query.toString()}`);
}

export async function getSubscriptionById(
  id: string
): Promise<{ data: AdminSubscription }> {
  return request(`/subscriptions/${id}`);
}

/**
 * Extend a granted trial.
 *
 * The path, the method and the body keys are unchanged from the shipped
 * client: `shared/src/types/admin.ts` has carried `'subscription.extend_trial'`
 * in the `AuditAction` union since before any of this, so the capability was
 * designed and typed and only the server was never written (D-001).
 *
 * Two things about the signature are not free choices:
 *
 * - **`reason` is required.** The whole reason the shipped `+7 Days Trial`
 *   button was worthless as an audit trail is that its caller supplied the
 *   constant `'Admin action'`. An optional reason is a reason that gets
 *   omitted.
 * - **Four fields come back, not two.** `previousTrialEndDate` and
 *   `countedFromToday` are what `ExtendTrialPanel` renders *instead of* its own
 *   preview once a real answer exists, which is the only thing that makes the
 *   two sides' agreement on the date checkable rather than merely careful. The
 *   two date fields are ISO strings on the wire, so the panel re-parses with
 *   `new Date(v)` before formatting.
 *
 * `success` is `boolean` rather than the server's `z.literal(true)`: the
 * shipped client already declared the wider type, it accepts the server's
 * `true`, and narrowing it is a change no consumer asked for.
 */
export async function extendTrial(
  id: string,
  additionalDays: number,
  reason: string
): Promise<{
  success: boolean;
  previousTrialEndDate: string;
  newTrialEndDate: string;
  countedFromToday: boolean;
}> {
  return request(`/subscriptions/${id}/extend-trial`, {
    method: 'POST',
    body: JSON.stringify({ additionalDays, reason }),
  });
}

export async function cancelSubscription(
  id: string,
  reason?: string
): Promise<{ success: boolean }> {
  return request(`/subscriptions/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function changeTier(
  id: string,
  tier: SubscriptionTier,
  reason?: string
): Promise<{ success: boolean; previousTier: string; newTier: string }> {
  return request(`/subscriptions/${id}/change-tier`, {
    method: 'POST',
    body: JSON.stringify({ tier, reason }),
  });
}
